import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertService } from '../../services/alert.service';
import { CompaniesService } from '../../services/companies.service';
import { AccountsService } from '../../services/accounts.service';
import Header from '../../components/layout/header.component';
import Asaid from '../../components/layout/asaid.component';
import Footer from '../../components/layout/footer.component';
import useBgPages from '../../hooks/use-bg-pages.hook';
import useRequest from '../../hooks/use-request.hook';
import Datatable from '../../components/datatable/datatable.component';
import EditAccount from './modal/edit-accounts.modal';
import Select from 'react-select';
import AccountStepper from './accounts.stepper';
import DeactivateAccount from './modal/accounts-deactivate.modal';
import Icon from '../../components/forms/icon.component';
import ChannelsConfigurator from './accounts-channels-configurator.partial';
import ActivateAccount from './modal/accounts-activate.modal';
import ActionsDropdown from '../../components/datatable/actions-dropdown.component';
import PrincipalCard from '../../components/cards/principal-card.component';
import DateRegister from '../../components/datatable/register-data.component';
import { useLocation, useNavigate } from 'react-router-dom';

interface Data {
  _id: string;
  created_at: string;
  status: boolean;
}
function AccountsPage() {
  useBgPages();
  const { t } = useTranslation();
  const { call, isRequesting } = useRequest();
  const [row, setRows] = useState<Data[]>([]);
  const [totalAccounts, setTotalAccounts] = useState();
  const [activeAccounts, setActiveAccounts] = useState();
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [companies, setCompanies] = useState<Array<{ value: string; label: string }>>([]);
  const [dataChannel, setDataChannel] = useState<any>({});
  const navigate = useNavigate();
  const location = useLocation();

  const isAddRoute = location.pathname.endsWith('/add');
  const isConfigureRoute = location.pathname.includes('/configure/');

  const columns = [
    {
      name: t('account-page.title-column-1'),
      sortable: true,
      selector: (r: any) => `${r.name} ${r.company?.name}`,
      cell: (row: any) => {
        return (
          <div className="d-flex flex-column">
            <span className="text-dark fs-5 fw-bolder">{row.name}</span>
            <span className="text-muted fw-bolder fs-6">{row.company?.name}</span>
          </div>
        );
      }
    },
    {
      name: t('account-page.title-column-2'),
      sortable: true,
      selector: (r: any) => `${r.created_at}`,
      cell: (row: any) => {
        return <DateRegister created_at={row.created_at} />;
      }
    },
    {
      name: t('account-page.title-column-3'),
      sortable: true,
      selector: (r: any) => `${r.status}`,
      cell: (row: any) =>
        row.status ? (
          <span className="badge text-dark fs-6">
            <Icon icon="ki-check-circle" size="fs-1" type="primary" />
            {t('general.active')}
          </span>
        ) : (
          <span className="badge text-danger fs-6">
            <Icon icon="ki-minus-circle" size="fs-1" type="danger" />
            {t('general.inactive')}
          </span>
        )
    },
    {
      name: t('account-page.active-channels'),
      width: 20,
      sortable: true,
      selector: (r: any) => `${r._id}`,
      cell: (row: any) => {
        const channelMapping = {
          whatsapp: { name: 'WhatsApp', icon: 'ki-whatsapp', color: 'primary' },
          rcs_conversational: { name: 'RCS Conversacional', icon: 'ki-messages', color: 'info' },
          rcs_single: { name: 'RCS Single', icon: 'ki-messages', color: 'info' },
          rcs_basic: { name: 'RCS Basic', icon: 'ki-messages', color: 'info' },
          sms: { name: 'SMS', icon: 'ki-message-notif', color: 'warning' },
          email: { name: 'Email', icon: 'ki-sms', color: 'primary' }
        };

        const activeChannels = Object.entries(row?.channels || {})
          .filter(
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            ([_, channelsArray]: any) => Array.isArray(channelsArray) && channelsArray.length > 0
          )
          .map(([key]) => channelMapping[key as keyof typeof channelMapping]);
        return (
          <div className="mw-350px">
            {activeChannels.length > 0 ? (
              <ul className="list-unstyled">
                {activeChannels.map((channel) => (
                  <li key={channel?.name} className="d-flex align-items-center mb-2">
                    <Icon icon={channel?.icon} size="fs-2" type={channel?.color} className="me-2" />
                    <span className="text-dark fs-6">{channel?.name}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <span className="text-gray-700 badge bg-secondary fs-6">
                <Icon icon="ki-information" size="fs-2" className="me-1" type="danger" />
                {t('account-page.no-active-channels')}
              </span>
            )}
          </div>
        );
      }
    },
    {
      name: t('account-page.title-column-4'),
      sortable: true,
      selector: (r: any) => `${r._id}`,
      exportable: false,
      cell: (row: any) => {
        return (
          <>
            <ActionsDropdown
              edit={() => setShowEditNameAccountModal({ show: true, data: row })}
              desactivateModal={
                row.status ? () => setShowDesactivateModal({ show: true, data: row }) : undefined
              }
              activateModal={
                !row.status ? () => setShowActivateModal({ show: true, data: row }) : undefined
              }
              channelSettings={
                row.status
                  ? () => navigate(`/accounts/channels-configurator/${row._id}`, { state: row })
                  : undefined
              }
            />
          </>
        );
      }
    }
  ];

  const getAccounts = async () => {
    try {
      const response = await call(() =>
        selectedCompanyId
          ? AccountsService.getAccountsForCompany(selectedCompanyId)
          : AccountsService.getAccounts()
      );
      setRows(response);
      setTotalAccounts(response?.length || 0);
      setActiveAccounts(response?.filter((x: any) => x.status === true).length || 0);
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
      setRows([]);
    }
  };

  const getCompanies = async () => {
    try {
      const data = await CompaniesService.getCompanies();
      setCompanies(
        data.map((item: any) => ({
          value: item._id,
          label: item.name,
          type: item.service_type
        }))
      );
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
      setCompanies([]);
    }
  };

  const [showEditNameAccountModal, setShowEditNameAccountModal] = useState({
    show: false,
    data: null
  });

  const handleShowModal = async (refresh: boolean) => {
    if (refresh) {
      await getAccounts();
    }
    setShowEditNameAccountModal({ show: false, data: null });
  };

  const [showDesactivateModal, setShowDesactivateModal] = useState({
    show: false,
    data: null
  });

  const handleShowModal1 = async (refresh: boolean) => {
    if (refresh) {
      await getAccounts();
    }
    setShowDesactivateModal({ show: false, data: null });
  };

  const [showActivateModal, setShowActivateModal] = useState({
    show: false,
    data: null
  });

  const handleShowActivateModal = async (refresh: boolean) => {
    if (refresh) {
      await getAccounts();
    }
    setShowActivateModal({ show: false, data: null });
  };

  let content;
  if (isAddRoute) {
    content = (
      <AccountStepper
        Companies={companies}
        onCancel={() => navigate('/accounts')}
        onSucess={() => {
          navigate('/accounts');
          getAccounts();
        }}
      />
    );
  } else if (isConfigureRoute) {
    content = (
      <ChannelsConfigurator
        payload={dataChannel}
        onSuccess={() => {
          navigate('/accounts');
          getAccounts();
        }}
      />
    );
  } else {
    content = (
      <AccountsView
        totalAccounts={totalAccounts}
        activeAccounts={activeAccounts}
        companies={companies}
        selectedCompanyId={selectedCompanyId}
        setSelectedCompanyId={setSelectedCompanyId}
        rows={row}
        columns={columns}
        isLoading={isRequesting}
        onAddAccount={() => navigate('/accounts/add')}
        onRefresh={() => getAccounts()}
        onConfigureChannels={(row: any) => {
          setDataChannel(row);
          navigate(`/accounts/channels-configurator/${row._id}`);
        }}
      />
    );
  }

  useEffect(() => {
    getAccounts();
    getCompanies();
  }, []);

  useEffect(() => {
    getAccounts();
  }, [selectedCompanyId]);

  return (
    <>
      <div className="page d-flex flex-row flex-column-fluid">
        <Asaid />
        <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
          <Header />
          <div className="content fs-6 d-flex flex-column flex-column-fluid" id="kt_content">
            <div className="post fs-6 d-flex flex-column-fluid mt-7" id="kt_post">
              <div className="container-xxl">{content}</div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
      <EditAccount
        show={showEditNameAccountModal.show}
        onRequestClose={(refresh: any) => handleShowModal(refresh)}
        payload={showEditNameAccountModal.data}
      />
      <DeactivateAccount
        show={showDesactivateModal.show}
        onRequestClose={(refresh: any) => handleShowModal1(refresh)}
        payload={showDesactivateModal.data}
      />
      <ActivateAccount
        show={showActivateModal.show}
        onRequestClose={(refresh: any) => handleShowActivateModal(refresh)}
        payload={showActivateModal.data}
      />
    </>
  );
}

const AccountsView = ({
  totalAccounts,
  activeAccounts,
  companies,
  selectedCompanyId,
  setSelectedCompanyId,
  rows,
  isLoading,
  onAddAccount,
  onRefresh,
  columns
}: any) => {
  const { t } = useTranslation();

  const dataStatistics = [
    { label: 'Cuentas', total: totalAccounts, type: '', icon: 'ki-book-open' },
    { label: 'Activos', total: activeAccounts, type: 'info', icon: 'ki-check-circle' }
  ];
  return (
    <>
      {/* 📌 Tarjeta de Estadísticas y Filtros */}
      <PrincipalCard
        dataStatistics={dataStatistics}
        btnCreate
        onClick={onAddAccount}
        btnRefresh
        onRefresh={async () => {
          await onRefresh();
          AlertService.toastSuccess('¡ Éxito !, información actualizada.');
        }}
        title={t('general.register')}
        titleBolder={t('account-page.title-accounts')}
        subtitle={t('account-page.subtitle-accounts')}
        breadcrumbItems={[t('asaid.link-title-operations')]}
        textButton={t('account-page.add-account-btn')}
        iconButton="ki-soft-2"
      />

      {/* 📌 Tabla de Cuentas */}
      <div className="row g-xl-12">
        <div className="col-xl-12 mb-5">
          <div className="card">
            <div className="card-header align-items-center border-0 mt-5">
              <div className="card-title align-items-start flex-column animate-fadeInUp animate-delay-1 h3">
                <span className="fw-bolder fw-bold fs-2">{t('asaid.link-accounts')}</span>
                <span className="text-gray-700 mt-2 fw-semibold fs-6">
                  {t('channels.wa.registred')}
                </span>
              </div>
              <div className="card-toolbar">
                <button
                  type="button"
                  className={`btn ${selectedCompanyId !== '' ? 'btn-light-primary' : 'btn-dark'} btn-center`}
                  data-kt-menu-trigger="{default: 'hover', lg: 'hover'}"
                  data-kt-menu-attach="parent"
                  data-kt-menu-placement="bottom-end"
                >
                  {selectedCompanyId !== '' ? (
                    <span className="fw-bolder">Filtros activados</span>
                  ) : (
                    <span className="fw-bolder">Filtros</span>
                  )}
                  <Icon icon="ki-filter" size="fs-2" className="hover-scale ms-2 fw-bolder" />
                </button>
                <div
                  className="menu menu-sub menu-sub-dropdown menu-column fw-semibold py-3 w-auto min-w-200px max-w-full text-nowrap w-350px bg-dropFilter"
                  data-kt-menu="true"
                  style={{ minWidth: '20rem' }}
                >
                  <div className="bg-dropFilter">
                    <div className="card-header card-header-content-between pb-2 pt-5">
                      <div className="fs-5 text-gray-900 fw-bolder mt-3">
                        Selecciona los filtros <br />
                        <span className="text-primary fs-7"></span>
                      </div>
                    </div>
                    <div className="card-body">
                      <div className="mb-7">
                        <label className="form-label">
                          {t('blacklist-page.company-label')}
                          <span className="fs-8 text-muted ms-1"></span>
                        </label>
                        <div className="row">
                          <div className="col">
                            <Select
                              options={companies}
                              noOptionsMessage={() => t('general.no-options')}
                              isClearable
                              className="form-select-custom"
                              classNamePrefix="form-select-custom"
                              placeholder={t('account-page.filter-company')}
                              onChange={(
                                selectedOption: { value: string; label: string } | null
                              ) => {
                                setSelectedCompanyId(selectedOption ? selectedOption.value : '');
                              }}
                              value={
                                companies.find(
                                  (company: any) => company.value === selectedCompanyId
                                ) || null
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body pt-1">
              <Datatable columns={columns} data={rows} isLoading={isLoading} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AccountsPage;
